import { useEffect, useState } from 'react'
import { Asset, AssetQuery, DateRange } from '../types'
import { isAfter, isBefore } from 'date-fns'
import { useSelector } from 'react-redux'
import { RootState } from '../redux'

export type AssetStatusOption = 'online' | 'offline' | 'virtual' | 'detached'

export interface UseAssetStatusOptions {
  query: AssetQuery
  onQueryChange?: (query: AssetQuery) => void
}

export function useAssetStatusOptions({ query, onQueryChange }: UseAssetStatusOptions) {
  const [selectedStatusOptions, setSelectedStatusOptions] = useState<AssetStatusOption[]>([])

  const options: AssetStatusOption[] = ['online', 'offline', 'virtual', 'detached']

  useEffect(() => {
    if (query?.status === true) {
      const newSelectedOptions: AssetStatusOption[] = ['online']

      if (query?.virtual === true) {
        newSelectedOptions.push('virtual')
      }

      if (query?.withDetached === true) {
        newSelectedOptions.push('detached')
      }

      setSelectedStatusOptions(newSelectedOptions)
    } else if (query?.status === false) {
      const newSelectedOptions: AssetStatusOption[] = ['offline']

      if (query?.virtual === true) {
        newSelectedOptions.push('virtual')
      }

      if (query?.withDetached === true) {
        newSelectedOptions.push('detached')
      }

      setSelectedStatusOptions(newSelectedOptions)
    } else if (query?.virtual === true) {
      const newSelectedOptions: AssetStatusOption[] = ['virtual']

      if (query?.withDetached === true) {
        newSelectedOptions.push('detached')
      }

      setSelectedStatusOptions(newSelectedOptions)
    } else if (query?.virtual === false) {
      const newSelectedOptions: AssetStatusOption[] = ['online', 'offline']

      if (query?.withDetached === true) {
        newSelectedOptions.push('detached')
      }

      setSelectedStatusOptions(newSelectedOptions)
    } else if (query?.withDetached === true) {
      const newSelectedOptions: AssetStatusOption[] = ['detached']

      if (!query?.detached) {
        newSelectedOptions.push('online', 'offline', 'virtual')
      }

      setSelectedStatusOptions(newSelectedOptions)
    } else {
      const newSelectedStatusOptions: AssetStatusOption[] = []

      if (selectedStatusOptions.length > 1) {
        newSelectedStatusOptions.push('online', 'offline', 'virtual')
      }

      setSelectedStatusOptions(newSelectedStatusOptions)
    }
  }, [query?.status, query?.virtual, query?.detached, query?.withDetached])

  function onSelectedStatusOptionsChange(values: AssetStatusOption[]): void {
    const withOnline = values.includes('online')
    const withOffline = values.includes('offline')
    const withVirtual = values.includes('virtual')
    const withDetached = values.includes('detached')

    if (withOnline && withOffline && withVirtual) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: undefined,
        detached: undefined,
        withDetached
      })
    } else if (withOnline && withOffline) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: false,
        detached: undefined,
        withDetached
      })
    } else if (withOnline && withVirtual) {
      onQueryChange?.({
        ...query,
        status: true,
        virtual: true,
        detached: undefined,
        withDetached
      })
    } else if (withOffline && withVirtual) {
      onQueryChange?.({
        ...query,
        status: false,
        virtual: true,
        detached: undefined,
        withDetached
      })
    } else if (withOnline) {
      onQueryChange?.({
        ...query,
        status: true,
        virtual: false,
        detached: undefined,
        withDetached
      })
    } else if (withOffline) {
      onQueryChange?.({
        ...query,
        status: false,
        virtual: false,
        detached: undefined,
        withDetached
      })
    } else if (withVirtual) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: true,
        detached: undefined,
        withDetached
      })
    } else if (withDetached) {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: undefined,
        withDetached: true,
        detached: true
      })
    } else {
      onQueryChange?.({
        ...query,
        status: undefined,
        virtual: undefined,
        detached: undefined,
        // FIXME: This should be the initial state of the query.
        withDetached: false
      })
    }
  }

  return {
    options,
    selectedStatusOptions,
    onSelectedStatusOptionsChange
  }
}

export function useAssetDateRange(asset?: Asset | null): DateRange {
  const { from, to } = useSelector((state: RootState) => state.query)

  const attachedAt =
    asset !== undefined && asset !== null
      ? asset.attachedAt !== undefined && asset.attachedAt !== null
        ? new Date(asset.attachedAt)
        : new Date(asset.createdAt)
      : from

  const detachedAt =
    asset !== undefined && asset !== null
      ? asset?.detachedAt !== undefined && asset.detachedAt !== null
        ? new Date(asset.detachedAt)
        : to
      : to

  return {
    from: isBefore(from, attachedAt) ? attachedAt : from,
    to: isAfter(to, detachedAt) ? detachedAt : to
  }
}
